import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  RangeTooLargeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApplyChargeBasedPricingPropagationMutation,
  useGetChargeBasedPricingByIdQuery,
  useLazyGetChargeBasedPricingPropagationPreviewQuery,
  useUpdateChargeBasedPricingMutation,
} from "store/queries/chargeBasedPricing";

import {
  ChargeBasedPricingForm,
  IChargeBasedPricingFormData,
  INITIAL_CHARGE_BASED_PRICING_FORM_DATA,
} from "./components/ChargeBasedPricingForm";
import { usePropagateAfterEdit } from "./components/usePropagateAfterEdit";

export function EditChargeBasedPricingPage() {
  useDocumentTitle("Edit Charge-Based Pricing Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetChargeBasedPricingByIdQuery(
    id ?? ""
  );
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateChargeBasedPricingMutation();
  const [formData, setFormData] = useState<IChargeBasedPricingFormData>(
    INITIAL_CHARGE_BASED_PRICING_FORM_DATA
  );

  const { openForId, modal: propagateModal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetChargeBasedPricingPropagationPreviewQuery as any,
    useApplyPropagation: useApplyChargeBasedPricingPropagationMutation as any,
    onDone: () => navigate("/payer-rules"),
  });

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      scope: ruleset.scope,
      mode: ruleset.mode ?? "REPLACE_FEE_SCHEDULE",
      multiplier: Number(ruleset.multiplier),
      maxAmountInDollars:
        ruleset.maxAmountInCents === null ||
        ruleset.maxAmountInCents === undefined
          ? null
          : ruleset.maxAmountInCents / 100,
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      // Same range collapse as lesser-of-charges so the editor shows "0272-0420"
      // instead of 149 individual chips after re-opening a saved rule.
      codes: collapseToRanges(ruleset.codes ?? []),
      excludedCodes: collapseToRanges(ruleset.excludedCodes ?? []),
      packagedCodes: collapseToRanges(ruleset.packagedCodes ?? []),
      groupName: ruleset.groupName ?? "",
      // CLE-2524: always present on loaded rows (backfilled).
      applicability: ruleset.applicability,
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    if (!formData.scope) return;
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    let codes: string[];
    let excludedCodes: string[];
    let packagedCodes: string[];
    try {
      codes = prepareCodesForSubmit(formData.codes);
      excludedCodes = prepareCodesForSubmit(formData.excludedCodes);
      packagedCodes = prepareCodesForSubmit(formData.packagedCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        scope: formData.scope,
        mode: formData.mode,
        multiplier: formData.multiplier,
        maxAmountInCents:
          formData.maxAmountInDollars === null
            ? null
            : Math.round(formData.maxAmountInDollars * 100),
        codes,
        excludedCodes,
        packagedCodes,
        groupName: formData.groupName.trim() || null,
        // CLE-2524: immutable — the locked form value matches the stored row.
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        // CLE-2597 — a FALLBACK rule may name a payer; only a TYPICAL template is scopeless.
        payerId: formData.applicability === "TYPICAL" ? null : formData.payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Charge-Based Pricing rule updated successfully.",
      });
      if (formData.applicability !== "SCOPED") {
        await openForId(id);
      } else {
        navigate("/payer-rules");
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to update charge-based pricing rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <ChargeBasedPricingForm
        title="Edit Charge-Based Pricing Rule"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      {propagateModal}
      <RuleHistory histories={data?.data.history} />
    </>
  );
}
